/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React from "react";
import {
  X,
  History,
  CheckCircle,
  AlertCircle,
  UserCheck,
  FileText,
  Clock,
} from "lucide-react";

const VettingHistoryModal = ({
  isOpen,
  onClose,
  vettingHistory = [],
  loadingHistory = false,
}) => {
  if (!isOpen) return null;

  const getVettingColor = (status) => {
    switch (status) {
      case "Active":
      case "Onboarded":
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800";
      case "Re_Examination":
      case "Rejected":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800";
      case "Interview":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800";
      case "Document_Verification":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800";
      case "Guarantee_And_Medical_Test":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border border-purple-200 dark:border-purple-800";
      case "Get_Exam":
        return "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600";
    }
  };

  const getVettingIcon = (status) => {
    switch (status) {
      case "Active":
      case "Onboarded":
        return <CheckCircle size={16} className="text-emerald-500" />;
      case "Re_Examination":
      case "Rejected":
        return <AlertCircle size={16} className="text-red-500" />;
      case "Interview":
        return <UserCheck size={16} className="text-blue-500" />;
      case "Document_Verification":
        return <FileText size={16} className="text-yellow-500" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      const options = {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };
      return date.toLocaleDateString("en-US", options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return formatDate(dateString);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-5 border-b border-gray-100 dark:border-dark-border flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-text dark:text-dark-text">
              Vetting History
            </h3>
            <p className="text-sm text-gray-500 dark:text-dark-muted mt-1">
              Status changes and timeline
            </p>
          </div>
          <button onClick={onClose}>
            <X
              size={20}
              className="text-gray-400 hover:text-text dark:hover:text-dark-text"
            />
          </button>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : vettingHistory.length > 0 ? (
            <div className="space-y-4">
              {vettingHistory.map((history, index) => (
                <div
                  key={index}
                  className="relative pl-8 pb-6 last:pb-0 border-l border-gray-200 dark:border-gray-700"
                >
                  <div className="absolute left-0 top-0 -translate-x-1/2 w-4 h-4 rounded-full bg-primary border-4 border-card dark:border-dark-surface"></div>
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        {getVettingIcon(history.status)}
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-bold ${getVettingColor(
                            history.status,
                          )}`}
                        >
                          {history.status}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-dark-muted">
                        {history.changedAt && (
                          <span className="block sm:inline sm:ml-2 text-xs text-gray-400">
                            {getTimeAgo(history.changedAt)}
                          </span>
                        )}
                      </span>
                    </div>
                    {history.changedBy && (
                      <div className="text-sm text-gray-600 dark:text-dark-muted mb-2">
                        Changed by:{" "}
                        <span className="font-medium text-text dark:text-dark-text">
                          {history.changedBy.name || "Admin"}
                        </span>
                      </div>
                    )}
                    {history.additionalNote && (
                      <div className="text-sm text-gray-600 dark:text-dark-muted">
                        <span className="font-medium">Note:</span>{" "}
                        {history.additionalNote}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-dark-muted">
              <History
                size={48}
                className="mx-auto mb-4 text-gray-300 dark:text-gray-600"
              />
              <p className="font-medium">No vetting history available</p>
              <p className="text-sm mt-1">No status changes recorded yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VettingHistoryModal;
